import * as React from 'react';
import { useState, useRef, createContext } from 'react';

/**
 * Several modal drawers can be open at once, so body scroll locking is ref-counted:
 * the body is only restored once every drawer that locked it has released it.
 */
let bodyScrollLocks = 0;
let bodyOverflowBeforeLock = '';

export function lockBodyScroll() {
  if (bodyScrollLocks === 0) {
    bodyOverflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  bodyScrollLocks += 1;
}

export function unlockBodyScroll() {
  if (bodyScrollLocks === 0) return;

  bodyScrollLocks -= 1;
  if (bodyScrollLocks === 0) {
    document.body.style.overflow = bodyOverflowBeforeLock;
  }
}

export const Context = createContext<{
  show: string[],
  toggleShow: Function,
  closeDrawer: (name: string) => void,
  closeDrawers: (names: string[]) => void,
}>({ show: [], toggleShow: () => {}, closeDrawer: () => {}, closeDrawers: () => {}});

interface Props {
  children: Node,
}

const DrawerProvider = (props: Props) => {
  const [show, setShow] = useState<string[]>([]);
  const showRef = useRef(show);
  showRef.current = show;

  // Functional update: this is also called from a Drawer's unmount cleanup,
  // where the `show` this closure captured may already be stale.
  function closeDrawer(name: string) {
    if (!showRef.current.includes(name)) return;

    setShow((current) => current.filter((item) => item !== name));
    dispatchDrawerCloseEvent(name);
  }

  function closeDrawers(names: string[]) {
    const filtered = [...show.filter((item) => names.indexOf(item) === -1 )];

    setShow(filtered);
  }

  /**
   * Dispatch close event of drawer
   * @param {string} id
   */
  function dispatchDrawerCloseEvent(id) {
    const customEvent = new CustomEvent('epub.drawer.close', {detail: {id}});
    document.dispatchEvent(customEvent);
  }

  /**
   *
   * @param {string | React.SyntheticEvent | undefined} id
   * @param  {boolean} allowMultiple
   */
  function toggleShow(id?: string | React.SyntheticEvent | undefined, allowMultiple?: boolean) {
    if (id && typeof id !== 'string') {
      id.stopPropagation();
    }

    if (typeof id !== 'string') {
      dispatchDrawerCloseEvent(show[0]);
      setShow([
        ...show.slice(0, show.length - 1),
      ]);
    } else if (show.indexOf(id) === -1 && allowMultiple && !show.includes(id)) {
      setShow([
        ...show,
        id,
      ]);
    } else if (show.indexOf(id) === -1 && !allowMultiple) {
      setShow([id]);
    }
  }

  return (
    <Context.Provider value={{ show, toggleShow, closeDrawer, closeDrawers }}>{props.children}</Context.Provider>
  );
};

export default DrawerProvider;
